import { useForm } from "./FormContext.jsx";
import DispatchingSingleLine from "./FormFields/DispatchingFields/DispatchingSingleLine.jsx";
import DispatchingMultiLine from "./FormFields/DispatchingFields/DispatchingMultiLine.jsx";
import DispatchingFileUpload from "./FormFields/DispatchingFields/DispatchingFileUpload.jsx";
import EnhancedProjectUpload from "./FormFields/DispatchingFields/DispatchingProjectUpload.jsx";
import styles from "./showroomForm.css";
import DispatchingSingleSelection from "./FormFields/DispatchingFields/DispatchingSingleSelection.jsx";
import DispatchingMultiSelection from "./FormFields/DispatchingFields/DispatchingMultiSelection.jsx";

/**
 * Represents a functional component that renders steps in a form process.
 *
 * @param {Object} params - The parameters required by the `Steps` component.
 * @param {function} params.validateFieldValue - Function to validate field values.
 * @param {Object} params.errors - Object holding error messages for the fields.
 * @param {function} params.setErrors - Function to update the error messages.
 * @param {Array} params.fields - Array of fields to render in the steps. A field comes from Sanity (CMS) and always has one of the following types: singleLine, multiLine, image, projectUpload, tags, and selection.
 * @param {function} params.isRequired - Function to determine if a field is required.
 * @param {boolean} [params.edit=false] - Indicates whether the form is in edit mode.
 * @param {Object} params.formData - The form state containing all field values.
 * @param {function} params.setFormData - Function to update the form state.
 * @param {function} params.validate - Function to perform general field validations.
 * @param {function} params.handleCheckboxChange - Function to handle changes in checkbox fields.
 * @param {boolean} params.isSubmitDisabled - Indicates if the form submission is disabled.
 * @param {boolean} params.isSubmissionPending - Indicates if a submission operation is in progress.
 *
 * @returns {JSX.Element} - Renders the form steps with field components based on the current step and field types.
 */
const Steps = ({
  validateFieldValue,
  errors,
  setErrors,
  fields,
  isRequired,
  edit = false,
  formData,
  setFormData,
  validate,
}) => {
  const { step } = useForm();

  const renderField = (field) => {
    if (field === "finish")
      return (
        <div className={styles.finishContainer}>
          <h1 className={styles.finish}>
            Du bist bereit euer Projekt hochzuladen!
          </h1>
        </div>
      );

    switch (field.type) {
      case "singleLine":
        return (
          <DispatchingSingleLine
            description={edit && field.description}
            key={field.key}
            label={field.label}
            formData={formData}
            fieldKey={field.key}
            required={isRequired(field.key)}
            error={errors[field.key]}
            setFormData={setFormData}
            validate={validate}
          />
        );
      case "image":
        return (
          <DispatchingFileUpload
            error={errors[field.key]}
            key={field.key}
            fieldKey={field.key}
            formData={formData}
            setFormData={setFormData}
            required={isRequired(field.key)}
            validateFile={validateFieldValue}
            label={field.label}
            description={edit && field.description}
            accept="image/png,image/jpeg,image/gif,image/webp"
          />
        );
      case "multiLine":
        return (
          <DispatchingMultiLine
            key={field.key}
            fieldKey={field.key}
            error={errors[field.key]}
            formData={formData}
            setFormData={setFormData}
            required={isRequired(field.key)}
            label={field.label}
            description={edit && field.description}
            validate={validate}
          />
        );
      case "projectUpload":
        return (
          <EnhancedProjectUpload
            key={field.key}
            description={edit && field.description}
            fieldKey={field.key}
            errors={errors}
            setErrors={setErrors}
            formData={formData}
            setFormData={setFormData}
            required={isRequired(field.key)}
            validateFile={validateFieldValue}
            label={field.label}
            validate={validate}
          />
        );
      case "tags":
        return (
          <DispatchingMultiSelection
            description={field.description}
            key={field.key}
            fieldKey={field.key}
            label={field.label}
            formData={formData}
            setFormData={setFormData}
            options={[
              ...(field.options?.map((o) => ({ value: o, label: o })) ?? []),
            ]}
            required={isRequired(field.key)}
            validate={validateFieldValue}
            error={errors[field.key]}
            edit={edit}
          />
        );
      case "selection":
        return (
          <DispatchingSingleSelection
            edit={edit}
            description={field.description}
            key={field.key}
            fieldKey={field.key}
            label={field.label}
            formData={formData}
            setFormData={setFormData}
            options={[
              { value: null, label: "" },
              ...(field.options?.map((o) => ({ value: o, label: o })) ?? []),
            ]}
            required={isRequired(field.key)}
            validate={validate}
            error={errors[field.key]}
          />
        );
      default:
        return <div>No component found</div>;
    }
  };

  return (
    <>
      {edit ? (
        <div className={styles.stepsVertical}>
          {fields.map((field) => renderField(field))}
        </div>
      ) : (
        <div className={styles.stepsConversational}>
          {renderField(fields[step - 1] ? fields[step - 1] : "finish")}
        </div>
      )}
    </>
  );
};

export default Steps;
